import { useState, useEffect, useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import GlobalContext from '../context/GlobalContext.js'
import postAPI from '../functions/API/postAPI.js'
import getAPI from '../functions/API/getAPI.js'
import getTenantTaskInstances from '../functions/API/getTenantTaskInstances.js'
import getTenantTaskInstance from '../functions/API/getTenantTaskInstance.js'
import getTaskMessages from '../functions/API/getTaskMessages.js'
import postSendTaskMessage from '../functions/API/postSendTaskMessage.js'
import postUpdateTaskInstanceStatus from '../functions/API/postUpdateTaskInstanceStatus.js'
import postCancelTaskInstance from '../functions/API/postCancelTaskInstance.js'
import TaskSubmittedValues, { stepHasSubmittedContent } from '../components/TaskSubmittedValues.jsx'
import { toast } from '../lib/toast.js'
import {
  FileText, Upload, CreditCard, Info, CheckCircle2, Clock, Lock,
  ChevronRight, ChevronDown, LogOut, MessageSquare, ListChecks, Send, AlertTriangle,
  ExternalLink, Home, Eye, XCircle,
} from 'lucide-react'

const PROGRESS_STAGES = [
  { key: 'submitted', label: 'Submitted', icon: CheckCircle2 },
  { key: 'review', label: 'In Review', icon: Eye },
  { key: 'complete', label: 'Ready', icon: Home },
]

const STEP_ICONS = {
  standard: FileText,
  document_upload: Upload,
  payment: CreditCard,
  information: Info,
}

// Animated pen-drawing path component
function DrawingPath({ d, delay, duration, strokeWidth = 2.5, pathLength = 300, color = 'currentColor', opacity = 1 }) {
  return (
    <path
      d={d}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={opacity}
      strokeDasharray={pathLength}
      strokeDashoffset={pathLength}
      style={{
        animation: `drawPath ${duration}ms cubic-bezier(0.33, 0, 0.67, 1) ${delay}ms forwards`,
      }}
    />
  );
}

// Animated house SVG with pen-drawing effect, door tease, smoke, and growing flowers
function DrawingHouse({ color }) {
  const t = 220;
  const ground    = { delay: 80,   duration: t * 2 };
  const leftWall  = { delay: ground.delay + t * 1.8,    duration: t * 1.8 };
  const rightWall = { delay: leftWall.delay + t * 1.2,  duration: t * 1.8 };
  const roofLeft  = { delay: rightWall.delay + t * 1.2, duration: t * 2 };
  const roofRight = { delay: roofLeft.delay + t * 1.6,  duration: t * 2 };
  const door      = { delay: roofRight.delay + t * 1.8, duration: t * 2.2 };
  const windowL   = { delay: door.delay + t * 1.8,      duration: t * 1.8 };
  const windowR   = { delay: windowL.delay + t * 1,     duration: t * 1.8 };
  const chimney   = { delay: windowR.delay + t * 1.2,   duration: t * 1.6 };
  const smoke     = { delay: chimney.delay + t * 1.5 };
  const flowerPot = { delay: smoke.delay + t * 1.2,     duration: t * 1.5 };
  const doorTease = { delay: flowerPot.delay + t * 4 };

  return (
    <svg width="100%" height="100%" viewBox="0 0 200 180">
      {/* Ground */}
      <DrawingPath d="M20 158 Q100 161 180 158" delay={ground.delay} duration={ground.duration} strokeWidth={2} pathLength={180} color="#D1D5DB" />
      {/* Left wall */}
      <DrawingPath d="M48 158 L48 88" delay={leftWall.delay} duration={leftWall.duration} pathLength={80} color={color} opacity={0.6} />
      {/* Right wall */}
      <DrawingPath d="M152 158 L152 88" delay={rightWall.delay} duration={rightWall.duration} pathLength={80} color={color} opacity={0.6} />
      {/* Roof left */}
      <DrawingPath d="M43 91 L100 38" delay={roofLeft.delay} duration={roofLeft.duration} strokeWidth={2.8} pathLength={85} color={color} opacity={0.7} />
      {/* Roof right */}
      <DrawingPath d="M100 38 L157 91" delay={roofRight.delay} duration={roofRight.duration} strokeWidth={2.8} pathLength={85} color={color} opacity={0.7} />
      {/* Door frame */}
      <DrawingPath d="M86 158 L86 118 Q86 112 92 112 L108 112 Q114 112 114 118 L114 158" delay={door.delay} duration={door.duration} strokeWidth={2} pathLength={120} color={color} opacity={0.6} />
      {/* Door interior glow */}
      <rect x="87" y="113" width="26" height="45" rx="2" fill={color}
        style={{ opacity: 0, animation: `doorGlow 1.6s ease-in-out ${doorTease.delay}ms infinite` }} />
      {/* Door panel — hinges on right side */}
      <g style={{ transformOrigin: '113px 135.5px', opacity: 0, animation: `doorTease 1.6s ease-in-out ${doorTease.delay}ms infinite` }}>
        <rect x="87" y="113" width="26" height="45" rx="2" fill="white" stroke={color} strokeWidth="2" opacity="0.15" />
        <circle cx="93" cy="137" r="2.5" stroke={color} strokeWidth="2" fill="none" opacity="0.5" />
      </g>
      {/* Left window */}
      <DrawingPath d="M58 108 L58 130 L78 130 L78 108 L58 108" delay={windowL.delay} duration={windowL.duration} strokeWidth={1.8} pathLength={90} color={color} opacity={0.5} />
      <DrawingPath d="M68 108 L68 130" delay={windowL.delay + t * 0.8} duration={t * 0.8} strokeWidth={1.8} pathLength={25} color={color} opacity={0.5} />
      <DrawingPath d="M58 119 L78 119" delay={windowL.delay + t * 1} duration={t * 0.8} strokeWidth={1.8} pathLength={25} color={color} opacity={0.5} />
      {/* Right window */}
      <DrawingPath d="M122 108 L122 130 L142 130 L142 108 L122 108" delay={windowR.delay} duration={windowR.duration} strokeWidth={1.8} pathLength={90} color={color} opacity={0.5} />
      <DrawingPath d="M132 108 L132 130" delay={windowR.delay + t * 0.8} duration={t * 0.8} strokeWidth={1.8} pathLength={25} color={color} opacity={0.5} />
      <DrawingPath d="M122 119 L142 119" delay={windowR.delay + t * 1} duration={t * 0.8} strokeWidth={1.8} pathLength={25} color={color} opacity={0.5} />
      {/* Chimney */}
      <DrawingPath d="M130 66 L130 46 L146 46 L146 79" delay={chimney.delay} duration={chimney.duration} strokeWidth={2} pathLength={70} color={color} opacity={0.5} />
      {/* Smoke wisps */}
      <path d="M137 44 Q141 36 136 28 Q132 22 137 14 Q140 8 136 2" stroke={color} strokeWidth={1.8} strokeLinecap="round" fill="none"
        style={{ strokeDasharray: 60, opacity: 0, animation: `smokeRise 2.5s ease-out ${smoke.delay}ms infinite` }} />
      <path d="M142 42 Q147 34 142 26 Q138 19 143 11 Q147 5 143 0" stroke={color} strokeWidth={1.8} strokeLinecap="round" fill="none"
        style={{ strokeDasharray: 60, opacity: 0, animation: `smokeRise 2.5s ease-out ${smoke.delay + 1000}ms infinite` }} />
      {/* Flower pot (static, draws once) */}
      <DrawingPath d="M18 158 L21 146 L37 146 L40 158" delay={flowerPot.delay} duration={flowerPot.duration} strokeWidth={1.8} pathLength={45} color={color} opacity={0.5} />
      <DrawingPath d="M19 146 L39 146" delay={flowerPot.delay + t * 0.5} duration={t * 0.6} strokeWidth={2} pathLength={22} color={color} opacity={0.5} />
      {/* Growing flowers (loop) */}
      <path d="M25 146 L25 130" stroke={color} strokeWidth={1.6} strokeLinecap="round" fill="none"
        style={{ strokeDasharray: 20, strokeDashoffset: 20, animation: `flowerGrow20 3s ease-out ${flowerPot.delay + t * 1}ms infinite` }} />
      <path d="M21 130 Q22 122 25 120 Q28 122 29 130 Q25 128 21 130" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" fill="none"
        style={{ strokeDasharray: 40, strokeDashoffset: 40, animation: `flowerGrow40 3s ease-out ${flowerPot.delay + t * 1.5}ms infinite` }} />
      <path d="M33 146 L33 134" stroke={color} strokeWidth={1.6} strokeLinecap="round" fill="none"
        style={{ strokeDasharray: 15, strokeDashoffset: 15, animation: `flowerGrow15 3s ease-out ${flowerPot.delay + t * 2}ms infinite` }} />
      <path d="M29 134 Q30 126 33 124 Q36 126 37 134 Q33 132 29 134" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" fill="none"
        style={{ strokeDasharray: 40, strokeDashoffset: 40, animation: `flowerGrow40 3s ease-out ${flowerPot.delay + t * 2.5}ms infinite` }} />
      <path d="M25 140 Q20 138 22 134" stroke={color} strokeWidth={1.4} strokeLinecap="round" fill="none"
        style={{ strokeDasharray: 15, strokeDashoffset: 15, animation: `flowerGrow15 3s ease-out ${flowerPot.delay + t * 3}ms infinite` }} />
    </svg>
  );
}

export default function OnboardingTaskList() {
  const { theme, themeCSS, settings, publicSettings } = useContext(GlobalContext)
  const navigate = useNavigate()

  const primaryColour = settings?.primary_hex_colour || '#1F2937'

  const [loading, setLoading] = useState(true)
  const [noInstances, setNoInstances] = useState(false)
  const [cancelled, setCancelled] = useState(false)
  const [instance, setInstance] = useState(null)
  const [completed, setCompleted] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const hasAutoStarted = useRef(false)
  const roleChecked = useRef(false)

  // Auto-scroll to the current outstanding step
  const currentStepRef = useRef(null)
  const hasAutoScrolledRef = useRef(false)

  // Cancel
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelMessage, setCancelMessage] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)

  // Tabs
  const [activeTab, setActiveTab] = useState('steps')

  // Completed steps expanded to view submitted data
  const [expandedSteps, setExpandedSteps] = useState({})

  // Messages
  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messageBody, setMessageBody] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const messagesFetched = useRef(false)
  const seenMessageCount = useRef(0)
  const messagesEndRef = useRef(null)

  // Load instance
  async function loadInitial() {
    try {
      const response = await getTenantTaskInstances()
      const instances = response.data?.task_instances || []

      if (!instances.length) {
        setNoInstances(true)
        setLoading(false)
        return
      }

      const inst = instances[0]
      if (inst.status === 'CANCELLED') {
        setCancelled(true)
        setLoading(false)
        return
      }
      if (inst.status === 'COMPLETED') {
        setCompleted(true)
        setInstance(inst)
        setLoading(false)
        return
      }

      // Fetch full instance with steps/fields
      await fetchInstance(inst.id)
      setLoading(false)
    } catch (e) {
      setNoInstances(true)
      setLoading(false)
    }
  }

  async function fetchInstance(id) {
    const instanceId = id || instance?.id
    if (!instanceId) return null

    const response = await getTenantTaskInstance(instanceId)
    if (response.response?.ok && response.data?.task_instance) {
      const inst = response.data.task_instance
      setInstance(inst)

      if (inst.status === 'COMPLETED') {
        setCompleted(true)
      }

      // Auto-start if NOT_STARTED
      if (inst.status === 'NOT_STARTED' && !hasAutoStarted.current) {
        hasAutoStarted.current = true
        postUpdateTaskInstanceStatus({ id: inst.id, status: 'IN_PROGRESS' })
      }

      return inst
    }
    return null
  }

  // Check for new messages (unread count) and pre-cache them
  async function checkForNewMessages() {
    if (!instance?.id) return
    const response = await getTaskMessages(instance.id)
    if (response.response?.ok && response.data?.messages) {
      const msgs = response.data.messages
      const adminMessages = msgs.filter(m => m.sender_role !== 'tenant')
      const unseen = adminMessages.length - seenMessageCount.current
      setUnreadCount(unseen > 0 ? unseen : 0)
      // Pre-cache messages so they show instantly when switching to the messages tab
      setMessages(msgs)
    }
  }

  // Fetch full messages (when Messages tab active)
  async function fetchMessages(isInitial) {
    if (!instance?.id) return
    if (isInitial) setMessagesLoading(true)

    const response = await getTaskMessages(instance.id)
    if (response.response?.ok && response.data?.messages) {
      setMessages(response.data.messages)
      const adminCount = response.data.messages.filter(m => m.sender_role !== 'tenant').length
      seenMessageCount.current = adminCount
      setUnreadCount(0)
    }
    setMessagesLoading(false)
  }

  async function handleSendMessage() {
    if (!messageBody.trim() || sendingMessage) return
    setSendingMessage(true)
    const response = await postSendTaskMessage({
      task_instance_id: instance.id,
      body: messageBody.trim(),
    })
    if (response.response?.ok) {
      setMessageBody('')
      await fetchMessages(false)
    } else {
      toast.error('Failed to send message')
    }
    setSendingMessage(false)
  }

  // Check role change (admin linked to agreement)
  async function checkRoleChange() {
    try {
      const response = await getAPI('token/verify')
      if (response.data?.role && response.data.role !== 'pre_tenancy' && !roleChecked.current) {
        roleChecked.current = true
        localStorage.setItem('role', response.data.role)
        window.location.href = '/home'
      }
    } catch (e) {
      // Silently ignore
    }
  }

  async function handleCancelInstance() {
    if (!cancelReason || cancelLoading) return
    setCancelLoading(true)
    const response = await postCancelTaskInstance({
      id: instance.id,
      cancellation_reason: cancelReason,
      cancellation_message: cancelMessage.trim() || null,
    })
    if (response.response?.ok) {
      setCancelModalOpen(false)
      if (response.data?.pending_approval) {
        toast.success('Cancellation request submitted for review.')
        await fetchInstance(instance.id)
      } else {
        toast.success('Your request has been cancelled.')
        setCancelled(true)
      }
    } else {
      toast.error(response.data?.message || 'Failed to cancel. Please try again.')
    }
    setCancelLoading(false)
  }

  async function handleRefresh() {
    setRefreshing(true)
    setNoInstances(false)
    setCancelled(false)
    setCompleted(false)
    setLoading(true)
    await loadInitial()
    setRefreshing(false)
  }

  async function logout() {
    const body = { '': '' }
    let destroy = await postAPI('token/destroy', body)
    if (destroy.response.ok) {
      localStorage.clear()
      window.location.href = '/login'
    }
  }

  // Initial load
  useEffect(() => {
    loadInitial()
  }, [])

  // Seed the seen count on first load so existing messages don't show as unread
  useEffect(() => {
    if (loading || !instance?.id) return
    async function seedMessages() {
      const response = await getTaskMessages(instance.id)
      if (response.response?.ok && response.data?.messages) {
        const msgs = response.data.messages
        const adminCount = msgs.filter(m => m.sender_role !== 'tenant').length
        seenMessageCount.current = adminCount
        setMessages(msgs)
      }
    }
    seedMessages()
    checkRoleChange()

    const interval = setInterval(() => {
      fetchInstance()
      checkRoleChange()
      if (activeTab !== 'messages') {
        checkForNewMessages()
      }
    }, 15000)
    return () => clearInterval(interval)
  }, [loading, instance?.id, activeTab])

  // Messages polling when tab active (5s)
  useEffect(() => {
    if (activeTab === 'messages' && instance?.id) {
      // Clear badge immediately — user is now viewing messages
      setUnreadCount(0)
      const adminCount = messages.filter(m => m.sender_role !== 'tenant').length
      seenMessageCount.current = adminCount

      if (!messagesFetched.current) {
        messagesFetched.current = true
        // Only show loading skeleton if we have no cached messages
        fetchMessages(messages.length === 0)
      }
      const interval = setInterval(() => fetchMessages(false), 5000)
      return () => clearInterval(interval)
    }
  }, [activeTab, instance?.id])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (activeTab === 'messages' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, activeTab])

  // Derived state
  const visibleSteps = instance?.steps
    ? [...instance.steps]
        .filter(s => s.is_conditionally_visible !== false && s.is_conditionally_visible !== 0)
        .sort((a, b) => (a.position ?? a.position_snapshot ?? 0) - (b.position ?? b.position_snapshot ?? 0))
    : []

  const completedCount = visibleSteps.filter(s => s.status === 'COMPLETED').length
  const totalSteps = visibleSteps.length

  const tenantSteps = visibleSteps.filter(s => s.actor_type_snapshot === 'tenant')
  const allTenantStepsDone = tenantSteps.length > 0 && tenantSteps.every(s =>
    s.status === 'COMPLETED' || s.status === 'PENDING_APPROVAL'
  )

  const canCancel = instance?.is_cancellable_snapshot &&
    ['NOT_STARTED', 'IN_PROGRESS', 'AWAITING_ADMIN'].includes(instance?.status) &&
    !instance?.cancellation_pending

  const isStepActionable = (step) => {
    if (step.actor_type_snapshot !== 'tenant') return false

    // The API is authoritative on step ordering — it knows which steps carry
    // requires_previous_step_complete and which admin steps hold the tenant up.
    // Responses from an older API omit the flag, so fall back to the status check.
    if (typeof step.is_actionable === 'boolean') return step.is_actionable

    return step.status !== 'COMPLETED' &&
      step.status !== 'BLOCKED' &&
      step.status !== 'PENDING_APPROVAL'
  }

  // The current outstanding step is the first actionable one in flow order
  const currentStep = visibleSteps.find(isStepActionable)

  // On load (including returning from a step, which remounts this page) scroll
  // the current outstanding step into view. Guarded to fire once per mount so
  // the 15s poll never yanks the viewport while the user is reading.
  useEffect(() => {
    if (loading || hasAutoScrolledRef.current) return
    const el = currentStepRef.current
    if (!el) return
    hasAutoScrolledRef.current = true
    const raf = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [loading, currentStep?.id])

  const getStepStatusInfo = (step) => {
    if (step.status === 'COMPLETED') return { badge: 'complete', text: 'Complete' }
    if (step.status === 'PENDING_APPROVAL') return { badge: 'pending', text: 'Under review' }
    if (step.status === 'BLOCKED') return { badge: 'locked', text: 'Requires a previous step' }
    if (step.blocked_reason === 'previous_step_incomplete') {
      return {
        badge: 'locked',
        text: step.blocked_by_step_name
          ? `Available once "${step.blocked_by_step_name}" is complete`
          : 'Requires a previous step',
      }
    }
    if (step.actor_type_snapshot === 'admin') return { badge: 'admin', text: 'Being handled by us' }
    return { badge: 'actionable', text: step.description_snapshot || 'Click to complete' }
  }

  const handleStepPress = (step) => {
    if (!isStepActionable(step)) return
    navigate(`/onboarding/step/${step.id}`)
  }

  const toggleStepExpanded = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }))
  }

  // Logo helper
  const Logo = () => {
    if (settings?.logo_url) {
      return (
        <img
          src={theme === 'dark' && settings.dark_mode_logo_url ? settings.dark_mode_logo_url : settings.logo_url}
          alt="logo"
          width="180"
          className="mx-auto"
        />
      )
    }
    return <h1 className="text-2xl font-semibold text-gray-900">{settings?.company_name}</h1>
  }

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColour }}></div>
      </div>
    )
  }

  // --- CANCELLED STATE ---
  if (cancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          <Logo />
          <div className="mt-8 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
              <XCircle className="h-7 w-7 text-gray-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Request Cancelled</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your onboarding request has been cancelled. If you believe this was a mistake, please contact us.
            </p>
            {publicSettings?.user_help_link && (
              <a
                href={publicSettings.user_help_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Help & Support
              </a>
            )}
          </div>
          <button
            onClick={logout}
            className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // --- NO INSTANCES / ERROR STATE ---
  if (noInstances) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          <Logo />
          <div className="mt-8 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 mb-4">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Oops!</h2>
            <p className="mt-2 text-sm text-gray-600">
              Something's gone wrong with your onboarding. Please contact customer services.
            </p>
            {publicSettings?.user_help_link && (
              <a
                href={publicSettings.user_help_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Help & Support
              </a>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: primaryColour }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={logout}
              className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- CLOSED STATE ---
  // A closed onboarding is finished but not successful. The wording stays neutral
  // because "close" is a generic outcome — completion_message carries the reason.
  if (completed && instance?.completion_outcome === 'negative') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          <Logo />
          <div className="mt-8 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 mb-4">
              <AlertTriangle className="h-7 w-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Onboarding closed</h2>
            <p className="mt-2 text-sm text-gray-600">
              {instance?.completion_message || 'Your onboarding has been closed. Please contact us if you have any questions.'}
            </p>
            {publicSettings?.user_help_link && (
              <a
                href={publicSettings.user_help_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Help & Support
              </a>
            )}
          </div>
          <button
            onClick={logout}
            className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // --- COMPLETED STATE ---
  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          <Logo />
          <div className="mt-8 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50 mb-4">
              <CheckCircle2 className="h-7 w-7 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Welcome home!</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your onboarding is complete. Your account is being finalised.
            </p>
          </div>
          <button
            onClick={logout}
            className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // --- MAIN ONBOARDING VIEW ---
  return (
    <div className="flex flex-col overflow-hidden" style={{ backgroundColor: primaryColour, height: '100dvh' }}>
      {/* Header */}
      <div>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="w-20" />
          <div className="flex-1 text-center">
            {settings?.logo_url ? (
              <img
                src={settings.dark_mode_logo_url || settings.logo_url}
                alt="logo"
                className="h-8 mx-auto brightness-0 invert"
              />
            ) : (
              <span className="text-lg font-semibold text-white">{settings?.company_name}</span>
            )}
          </div>
          <div className="w-20 flex justify-end">
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="max-w-2xl mx-auto px-4 pb-5">
          <div className="flex rounded-xl p-1" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <button
              onClick={() => setActiveTab('steps')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'steps'
                  ? 'text-white'
                  : 'text-white/50 hover:text-white/70'
              }`}
              style={activeTab === 'steps' ? { backgroundColor: 'rgba(255,255,255,0.25)' } : undefined}
            >
              <ListChecks className="h-4 w-4" />
              Steps
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors relative ${
                activeTab === 'messages'
                  ? 'text-white'
                  : 'text-white/50 hover:text-white/70'
              }`}
              style={activeTab === 'messages' ? { backgroundColor: 'rgba(255,255,255,0.25)' } : undefined}
            >
              <MessageSquare className="h-4 w-4" />
              Messages
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white text-[11px] font-bold px-1"
                  style={{ color: primaryColour }}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto flex flex-col bg-white rounded-t-[24px] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        {activeTab === 'steps' ? (
          <div className="w-full max-w-2xl mx-auto px-6 pt-6 pb-6">
            {allTenantStepsDone ? (
              /* Holding / Waiting State */
              <div className="text-center py-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Reviewing your information</h2>
                  <p className="mt-2 text-sm text-gray-500">
                    We're reviewing your submission and getting everything ready.
                  </p>
                </div>

                {/* Animated House Drawing */}
                <div className="mx-auto mb-8 w-48 h-40">
                  <DrawingHouse color={primaryColour} />
                </div>

                {/* Progress Tracker */}
                <div className="flex items-start justify-center gap-0 px-0">
                  {PROGRESS_STAGES.map((stage, index) => {
                    const isComplete = index === 0
                    const isCurrent = index === 1
                    const StageIcon = stage.icon

                    return (
                      <div key={stage.key} className="flex items-start">
                        {index > 0 && (
                          <div
                            className="h-0.5 w-8 sm:w-12 mt-[18px]"
                            style={{ backgroundColor: isComplete ? primaryColour : '#E5E7EB' }}
                          />
                        )}
                        <div className="flex flex-col items-center w-16 sm:w-20">
                          <div
                            className="flex h-9 w-9 items-center justify-center rounded-full border-2"
                            style={
                              isComplete
                                ? { backgroundColor: primaryColour + '20', borderColor: primaryColour }
                                : isCurrent
                                  ? { backgroundColor: primaryColour + '10', borderColor: primaryColour, boxShadow: `0 0 0 3px ${primaryColour}20` }
                                  : { backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }
                            }
                          >
                            {isComplete ? (
                              <CheckCircle2 className="h-4 w-4" style={{ color: primaryColour }} />
                            ) : isCurrent ? (
                              <Clock className="h-4 w-4" style={{ color: primaryColour }} />
                            ) : (
                              <StageIcon className="h-4 w-4 text-gray-300" />
                            )}
                          </div>
                          <span
                            className={`text-xs mt-1.5 font-medium ${
                              isComplete || isCurrent ? '' : 'text-gray-300'
                            }`}
                            style={isComplete || isCurrent ? { color: primaryColour } : undefined}
                          >
                            {stage.label}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Unread messages banner */}
                {unreadCount > 0 && (
                  <button
                    onClick={() => setActiveTab('messages')}
                    className="mt-8 inline-flex items-center gap-2.5 rounded-xl px-5 py-3 text-sm font-medium text-white"
                    style={{ backgroundColor: primaryColour }}
                  >
                    <MessageSquare className="h-4 w-4" />
                    {unreadCount} new {unreadCount === 1 ? 'message' : 'messages'}
                    <ChevronRight className="h-4 w-4 opacity-60" />
                  </button>
                )}
              </div>
            ) : (
              /* Normal Steps View */
              <>
                {/* Progress */}
                <div className="text-center mb-2">
                  <p className="text-sm text-gray-500">
                    {completedCount} of {totalSteps} steps complete
                  </p>
                  <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        backgroundColor: primaryColour,
                        width: totalSteps > 0 ? `${(completedCount / totalSteps) * 100}%` : '0%',
                      }}
                    />
                  </div>
                </div>

                <p className="text-center text-sm text-gray-500 mb-6 mt-4">
                  Complete each section below to finish your onboarding.
                </p>

                {/* Cancellation pending banner */}
                {instance?.cancellation_pending && (
                  <div className="flex items-center gap-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 mb-4">
                    <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Cancellation pending</p>
                      <p className="text-xs text-amber-600">Your cancellation request is being reviewed</p>
                    </div>
                  </div>
                )}

                {/* Step Cards */}
                <div className="space-y-3">
                  {visibleSteps.map((step) => {
                    const actionable = isStepActionable(step)
                    const { badge, text } = getStepStatusInfo(step)
                    const StepIcon = STEP_ICONS[step.step_type_snapshot] || FileText
                    const expandable = badge === 'complete' && stepHasSubmittedContent(step)
                    const isExpanded = expandable && !!expandedSteps[step.id]
                    const clickable = actionable || expandable

                    return (
                      <div
                        key={step.id}
                        ref={step.id === currentStep?.id ? currentStepRef : null}
                        className={`scroll-mt-4 rounded-2xl transition-colors ${
                          actionable
                            ? 'bg-white shadow-sm ring-1 ring-gray-200 hover:ring-gray-300 hover:shadow-md'
                            : badge === 'complete'
                              ? 'bg-white shadow-sm ring-1 ring-gray-200'
                              : 'bg-gray-50 ring-1 ring-gray-100 opacity-60'
                        }`}
                      >
                        <button
                          onClick={() => expandable ? toggleStepExpanded(step.id) : handleStepPress(step)}
                          disabled={!clickable}
                          className={`w-full flex items-center gap-4 rounded-2xl p-4 text-left ${clickable ? 'cursor-pointer' : ''}`}
                        >
                          <div
                            className="flex h-11 w-11 items-center justify-center rounded-xl"
                            style={
                              badge === 'complete'
                                ? { backgroundColor: '#F0FDF4' }
                                : actionable
                                  ? { backgroundColor: primaryColour + '12' }
                                  : { backgroundColor: '#F3F4F6' }
                            }
                          >
                            <StepIcon
                              className="h-5 w-5"
                              style={
                                badge === 'complete'
                                  ? { color: '#16A34A' }
                                  : actionable
                                    ? { color: primaryColour }
                                    : { color: '#9CA3AF' }
                              }
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${
                              badge === 'complete' || actionable ? 'text-gray-900' : 'text-gray-400'
                            }`}>
                              {step.name_snapshot}
                            </p>
                            <p className={`text-xs mt-0.5 truncate ${
                              badge === 'complete' ? 'text-green-600 font-medium' : 'text-gray-500'
                            }`}>
                              {expandable ? (isExpanded ? 'Hide submitted details' : 'Complete · tap to view details') : text}
                            </p>
                          </div>

                          {expandable ? (
                            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          ) : badge === 'complete' ? (
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500">
                              <CheckCircle2 className="h-4 w-4 text-white" />
                            </div>
                          ) : badge === 'locked' ? (
                            <Lock className="h-5 w-5 text-gray-300" />
                          ) : badge === 'pending' || badge === 'admin' ? (
                            <Clock className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-4 pt-3 border-t border-gray-100">
                            <TaskSubmittedValues
                              step={step}
                              labelClassName="text-gray-500"
                              valueClassName="text-gray-900"
                              mutedClassName="text-gray-400"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Cancel request */}
                {canCancel && (
                  <button
                    onClick={() => { setCancelReason(''); setCancelMessage(''); setCancelModalOpen(true); }}
                    className="mt-4 w-full text-center py-3 text-sm text-gray-400 hover:text-gray-500"
                  >
                    Cancel Request
                  </button>
                )}

                {/* Unread messages banner */}
                {unreadCount > 0 && (
                  <button
                    onClick={() => setActiveTab('messages')}
                    className="mt-4 w-full flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-white"
                    style={{ backgroundColor: primaryColour }}
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span className="flex-1 text-left">{unreadCount} new {unreadCount === 1 ? 'message' : 'messages'}</span>
                    <ChevronRight className="h-4 w-4 opacity-60" />
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          /* Messages Tab */
          <div className="max-w-2xl mx-auto w-full flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColour }}></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-500">No messages yet</p>
                  <p className="text-xs text-gray-400 mt-1">Send a message to start the conversation</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => {
                    const isTenant = msg.sender_role === 'tenant'
                    return (
                      <div
                        key={msg.id}
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          isTenant
                            ? 'ml-auto text-white'
                            : 'mr-auto bg-white ring-1 ring-gray-200'
                        }`}
                        style={isTenant ? { backgroundColor: primaryColour } : undefined}
                      >
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className={`text-xs font-semibold ${isTenant ? 'text-white/70' : 'text-gray-700'}`}>
                            {isTenant ? 'You' : msg.sender_name || 'Admin'}
                          </span>
                          <span className={`text-[11px] ${isTenant ? 'text-white/50' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        </div>
                        {msg.subject && (
                          <p className={`text-sm font-semibold mb-0.5 ${isTenant ? 'text-white' : 'text-gray-900'}`}>
                            {msg.subject}
                          </p>
                        )}
                        <p className={`text-sm whitespace-pre-wrap ${isTenant ? 'text-white/90' : 'text-gray-700'}`}>
                          {msg.body}
                        </p>
                      </div>
                    )
                  })}
                  {messages.length > 0 && (
                    <p className="text-[11px] text-gray-400 text-center pt-2">
                      Messages are reviewed in the order that they are received.
                    </p>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="sticky bottom-0 border-t border-gray-200 bg-white px-4 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ maxHeight: '100px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageBody.trim() || sendingMessage}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-white disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  style={{ backgroundColor: primaryColour }}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cancel request</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': primaryColour }}
                >
                  <option value="">Select a reason...</option>
                  {(instance?.cancellation_reasons_snapshot || []).map((reason, i) => (
                    <option key={i} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional details (optional)</label>
                <textarea
                  value={cancelMessage}
                  onChange={(e) => setCancelMessage(e.target.value)}
                  rows={3}
                  placeholder="Tell us more..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                disabled={cancelLoading}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Go back
              </button>
              <button
                onClick={handleCancelInstance}
                disabled={!cancelReason || cancelLoading}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
